// Pure derivation of the Portal navbar's display name from a UserMeResponse
// payload (the v2 shape shipped in 4.9).
//
// Per Decision **D6** (Documentation/Plans/DiscordIntegration.md Section B),
// the navbar follows whichever provider authorized the current session — NOT
// a user-overridable name. The server emits `via_provider` for this purpose:
//   - `'twitch'`  → `providers.twitch.username`
//   - `'discord'` → `providers.discord.global_name ?? providers.discord.username`
//
// We deliberately do NOT use `me.display_name` (the top-level field 4.9
// returns) because 4.9's `UserService.getMe` hardcodes it to
// `providers.twitch.username` regardless of `via_provider` — meaning a user
// logged in via Discord would see their Twitch username (or `null`) in the
// navbar, which violates 2.7's intent. Deriving here keeps the navbar
// honest without requiring a server-side change.
//
// Returns `''` (empty string) when:
//   - `me` is null / undefined (fetchMe hasn't resolved yet, or post-logout)
//   - the active provider isn't linked on the User (defensive; shouldn't
//     happen post-auth, but a partially-deleted account is recoverable as
//     "no name shown" rather than throwing)
//
// Module format: CommonJS (`module.exports`). The client imports this via
// `import { deriveNavbarDisplay } from '@/modules/user/displayName'` —
// webpack + Babel resolve that against the `.deriveNavbarDisplay` property
// of the CJS export, which is standard ESM/CJS interop. Using CJS (instead
// of `export function`) lets the server's Jest `require()` the file
// directly without needing a `.js`-transform — the client has no test
// runner of its own, so we co-test from the server.
function deriveNavbarDisplay(me) {
    if (!me) return '';
    const providers = me.providers || {};
    if (me.via_provider === 'discord') {
        const d = providers.discord;
        if (!d) return '';
        return d.global_name != null ? d.global_name : (d.username || '');
    }
    // 'twitch' or any unknown value falls back to Twitch. Defensive — the
    // server constrains `via_provider` to the two enum values, but treating
    // unknown as Twitch matches the pre-2.2 default (the project shipped
    // Twitch-only for its entire history before 2.2).
    const t = providers.twitch;
    if (!t) return '';
    return t.username || '';
}

module.exports = { deriveNavbarDisplay };
